import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { CollapsibleTabItem } from '../CollapsibleTabs.types';

/** Sub-pixel rounding cushion when comparing measured vs. available width. */
const FIT_TOLERANCE_PX = 1;

interface FitsExpandedResult {
  /** Positioning wrapper whose available inline width gates the expansion. */
  rootRef: RefObject<HTMLDivElement | null>;
  /** Hidden, always-expanded twin used to measure the natural total width. */
  measureRef: RefObject<HTMLDivElement | null>;
  /** True when every label fits on a single row without horizontal scroll. */
  expandAll: boolean;
}

/**
 * Decides whether all tab labels can be shown at once. Compares the natural
 * width of a hidden, fully-expanded twin (`measureRef`) against the available
 * width of the visible container (`rootRef`). Re-measures on container resize
 * and whenever the tab set changes (signature dependency) — e.g. tabs added or
 * the active language swapped (label strings change).
 */
export function useFitsExpanded(
  items: CollapsibleTabItem[]
): FitsExpandedResult {
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [expandAll, setExpandAll] = useState(false);

  const itemsSignature = items
    .map((item) => `${item.id}:${item.label}:${item.indicator ? 1 : 0}`)
    .join('|');

  useLayoutEffect(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;

    const check = () => {
      setExpandAll(measure.offsetWidth + FIT_TOLERANCE_PX <= root.clientWidth);
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(root);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [itemsSignature]);

  return { rootRef, measureRef, expandAll };
}
